/**
 * ChatbotDesigner Utilities
 * 
 * Extracted modules for:
 *  1. UUID generation (replaces Date.now() IDs)
 *  2. Modal confirmation system (replaces window.confirm/alert)
 *  3. Auto-save with debounce
 *  4. Undo/Redo state management
 *  5. Workflow validation
 */

import { useRef, useCallback, useEffect, useState} from 'react';

// ====================================================================
// 1. UUID Generation
// ====================================================================

/**
 * Generate a collision-safe unique ID.
 * Uses crypto.randomUUID() with fallback for older browsers.
 */
export function generateId(prefix = 'id') {
  const uuid = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 8)
    : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
  return `${prefix}_${uuid}`;
}


// ====================================================================
// 2. Auto-Save Hook
// ====================================================================

/**
 * useAutoSave — Debounced auto-save hook.
 * 
 * Usage:
 *   const { triggerSave, isSaving, lastSaved } = useAutoSave(saveFn, 3000);
 *   // Call triggerSave() whenever nodes/edges change
 *   // It will debounce and call saveFn after 3s of inactivity
 */
export function useAutoSave(saveFn, delayMs = 3000) {
  const timerRef = useRef(null);
  const isSavingRef = useRef(false);
  const lastSavedRef = useRef(null);

  const triggerSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      try {
        await saveFn();
        lastSavedRef.current = new Date();
      } catch (err) {
        console.error('[AutoSave] Failed:', err);
      } finally {
        isSavingRef.current = false;
      }
    }, delayMs);
  }, [saveFn, delayMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    triggerSave,
    isSaving: isSavingRef.current,
    lastSaved: lastSavedRef.current
  };
}


// ====================================================================
// 3. Undo/Redo Hook
// ====================================================================

/**
 * useUndoRedo — State history management for ReactFlow nodes/edges.
 * 
 * Usage:
 *   const { pushState, undo, redo, canUndo, canRedo } = useUndoRedo();
 *   // Call pushState({ nodes, edges }) after each user action
 *   // Call undo()/redo() from toolbar buttons or Ctrl+Z/Ctrl+Y
 */
export function useUndoRedo(maxHistory = 50) {
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);

  const pushState = useCallback((state) => {
    setPast(prev => {
      const next = [...prev, state];
      return next.length > maxHistory ? next.slice(-maxHistory) : next;
    });
    setFuture([]); // Clear redo stack on new action
  }, [maxHistory]);

  const undo = useCallback(() => {
    if (past.length === 0) return null;

    const previous = past[past.length - 1];
    setPast(prev => prev.slice(0, -1));
    return previous;
  }, [past]);

  const redo = useCallback(() => {
    if (future.length === 0) return null;

    const next = future[future.length - 1];
    setFuture(prev => prev.slice(0, -1));
    return next;
  }, [future]);

  // Call this before undo to preserve current state for redo
  const prepareUndo = useCallback((currentState) => {
    setFuture(prev => [...prev, currentState]);
  }, []);

  return {
    pushState,
    undo,
    redo,
    prepareUndo,
    canUndo: past.length > 0,
    canRedo: future.length > 0
  };
}


// ====================================================================
// 4. Workflow Validation
// ====================================================================

/**
 * validateWorkflow — Checks for common issues before save/publish.
 * 
 * Returns: { isValid: boolean, errors: string[], warnings: string[] }
 */
export function validateWorkflow(nodes, edges) {
  const errors = [];
  const warnings = [];

  // Must have at least one node
  if (!nodes || nodes.length === 0) {
    errors.push('Workflow has no steps. Add at least one step.');
    return { isValid: false, errors, warnings };
  }

  // Check for start node (node with no incoming edges)
  const targetNodeIds = new Set(edges.map(e => e.target));
  const startNodes = nodes.filter(n => !targetNodeIds.has(n.id));

  if (startNodes.length === 0) {
    errors.push('No start node found. At least one node must have no incoming connections.');
  } else if (startNodes.length > 1) {
    warnings.push(
      `Multiple start nodes detected: ${startNodes.map(n => n.data?.stepData?.name || n.id).join(', ')}. Only the first will be used.`
    );
  }

  // Check each node
  const sourceNodeIds = new Set(edges.map(e => e.source));

  nodes.forEach(node => {
    const stepData = node.data?.stepData || {};
    const name = stepData.name || node.id;

    // Empty message
    if (!stepData.message && !['carousel'].includes(stepData.message_type)) {
      errors.push(`"${name}" has no message content.`);
    }

    // Buttons/options with no text
    (stepData.buttons || []).forEach((btn, i) => {
      if (!btn.text || !btn.text.trim()) {
        errors.push(`"${name}" → Button ${i + 1} has no text.`);
      }
    });

    (stepData.options || []).forEach((opt, i) => {
      if (!opt.text || !opt.text.trim()) {
        errors.push(`"${name}" → Option ${i + 1} has no text.`);
      }
    });

    // Disconnected node (no outgoing edges and not an end node)
    const hasButtons = (stepData.buttons || []).length > 0;
    const hasOptions = (stepData.options || []).length > 0;
    const hasLogic = stepData.action?.type === 'logic';
    const hasNextStep = !!stepData.next_step;

    if (!sourceNodeIds.has(node.id) && !hasButtons && !hasOptions && !hasNextStep && !hasLogic) {
      // This is a terminal node — fine
    } else if (!sourceNodeIds.has(node.id) && (hasButtons || hasOptions) && !hasNextStep) {
      // Has buttons/options but no edges — check if buttons have next_step
      const allButtonsConnected = (stepData.buttons || []).every(b => b.next_step);
      const allOptionsConnected = (stepData.options || []).every(o => o.next_step);
      if (!allButtonsConnected && !allOptionsConnected) {
        warnings.push(`"${name}" has buttons/options but some are not connected to a next step.`);
      }
    }

    // Carousel items without titles
    (stepData.carousel_items || []).forEach((item, i) => {
      if (!item.title || !item.title.trim()) {
        warnings.push(`"${name}" → Carousel item ${i + 1} has no title.`);
      }
    });

    // WhatsApp Flow node without flow_id
    if (stepData.message_type === 'whatsapp_flow') {
      if (!stepData.flow_id) {
        errors.push(`"${name}" is a WhatsApp Flow node but has no Flow ID configured.`);
      }
    }

    // AI Response node without system prompt
    if (stepData.message_type === 'ai_response') {
      if (!stepData.ai_config?.system_prompt) {
        warnings.push(`"${name}" is an AI Response node with no system prompt — it will use a default prompt.`);
      }
    }

    // Handoff node without department
    if (stepData.message_type === 'handoff') {
      if (!stepData.handoff_config?.department) {
        warnings.push(`"${name}" is a Handoff node with no department set — it will default to "general".`);
      }
    }

    // Validation rules with empty error messages
    if (stepData.validation_rules) {
      Object.entries(stepData.validation_rules).forEach(([varName, rule]) => {
        if (rule.pattern && !rule.error_message) {
          warnings.push(`"${name}" has a validation pattern for "${varName}" but no error message — a default message will be shown.`);
        }
      });
    }

    // WhatsApp button compatibility checks
    const buttons = stepData.buttons || [];
    if (buttons.length > 10) {
      errors.push(`"${name}" has ${buttons.length} buttons — WhatsApp supports max 10 (list message). Reduce to 10 or fewer.`);
    } else if (buttons.length > 3) {
      // Info-level: will auto-promote to list, check row title limits (24 chars)
      buttons.forEach((btn, i) => {
        const waLabel = btn.wa_text || btn.text;
        if (waLabel.length > 24) {
          warnings.push(`"${name}" → Button ${i + 1} "${waLabel.slice(0, 20)}..." is ${waLabel.length} chars — WhatsApp list rows max 24. Add a wa_text short label.`);
        }
      });
    } else if (buttons.length > 0) {
      buttons.forEach((btn, i) => {
        const waLabel = btn.wa_text || btn.text;
        if (waLabel.length > 20 && !btn.wa_text) {
          warnings.push(`"${name}" → Button ${i + 1} "${btn.text.slice(0, 20)}..." exceeds 20 chars — will be truncated on WhatsApp. Consider adding a short label.`);
        }
      });
    }

    // Webhook action without URL
    if (stepData.action?.type === 'webhook') {
      if (!stepData.action.config?.url) {
        errors.push(`"${name}" has a Webhook action but no URL configured.`);
      }
    }
  });

  // Check for circular references (simple DFS cycle detection)
  const adjacency = {};
  edges.forEach(e => {
    if (!adjacency[e.source]) adjacency[e.source] = [];
    adjacency[e.source].push(e.target);
  });

  const visited = new Set();
  const inStack = new Set();

  function hasCycle(nodeId) {
    if (inStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;

    visited.add(nodeId);
    inStack.add(nodeId);

    for (const neighbor of adjacency[nodeId] || []) {
      if (hasCycle(neighbor)) return true;
    }

    inStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (hasCycle(node.id)) {
      warnings.push('Circular reference detected in workflow. This may cause infinite loops.');
      break;
    }
  }

  // Edge targets must exist (by ID or by label/name)
  const nodeIds = new Set(nodes.map(n => n.id));
  const nodeLabels = new Set();
  nodes.forEach(n => {
    const label = n.data?.label || n.data?.stepData?.name;
    if (label) nodeLabels.add(label);
  });
  const validRefs = new Set([...nodeIds, ...nodeLabels]);

  edges.forEach(e => {
    if (!validRefs.has(e.target)) {
      errors.push(`Edge from "${e.source}" points to non-existent node "${e.target}".`);
    }
    if (!validRefs.has(e.source)) {
      errors.push(`Edge to "${e.target}" comes from non-existent node "${e.source}".`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}


// ====================================================================
// 5. Channel type helpers
// ====================================================================

/**
 * All supported channel types and their display metadata.
 */
export const CHANNEL_TYPES = [
  { value: 'web', label: 'Web Widget', icon: '🌐' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { value: 'whatsapp_flow', label: 'WhatsApp Flows', icon: '📋' },
  { value: 'telegram', label: 'Telegram', icon: '✈️' },
  { value: 'instagram', label: 'Instagram DM', icon: '📸' },
  { value: 'facebook', label: 'Facebook Messenger', icon: '💬' },
  { value: 'api', label: 'Custom API', icon: '🔌' },
];

/**
 * All supported node/step types for the designer.
 */
export const STEP_TYPES = [
  { value: 'text', label: 'Text Message', icon: 'MessageSquare', color: 'blue' },
  { value: 'buttons', label: 'Buttons', icon: 'MousePointer', color: 'green' },
  { value: 'options', label: 'Quick Replies', icon: 'List', color: 'purple' },
  { value: 'image', label: 'Image', icon: 'Image', color: 'orange' },
  { value: 'video', label: 'Video', icon: 'Video', color: 'red' },
  { value: 'audio', label: 'Audio', icon: 'BoomBox', color: 'yellow' },
  { value: 'carousel', label: 'Carousel', icon: 'Layers', color: 'pink' },
  { value: 'whatsapp_flow', label: 'WhatsApp Flow', icon: 'FileText', color: 'teal' },
  { value: 'ai_response', label: 'AI Response', icon: 'Bot', color: 'violet' },
  { value: 'handoff', label: 'Human Handoff', icon: 'UserCheck', color: 'amber' },
  { value: 'file_upload', label: 'File Upload', icon: 'Paperclip', color: 'cyan' },
  { value: 'delay', label: 'Delay / Scheduled', icon: 'Clock', color: 'rose' },
  { value: 'ab_test', label: 'A/B Test Split', icon: 'GitBranch', color: 'indigo' },
  { value: 'sheet_read', label: 'Sheet Read (Bulk Send)', icon: 'Download', color: 'lime' },
  { value: 'sheet_write', label: 'Sheet Write', icon: 'Upload', color: 'rose' },
  { value: 'table_read', label: 'Table Read (Local DB)', icon: 'Database', color: 'sky' },
  { value: 'table_write', label: 'Table Write (Local DB)', icon: 'DatabaseBackup', color: 'teal' },
  { value: 'request_callback', label: 'Request Callback', icon: 'PhoneCall', color: 'orange' },
  { value: 'eod_sync_job', label: 'EOD Sync Job', icon: 'RefreshCw', color: 'indigo' },
  { value: 'reminder_job', label: 'Reminder Job', icon: 'Bell', color: 'violet' },
];
